import React, { forwardRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { PERIOD_OPTIONS } from '../../utils/dateUtils';

const parseYmd = (ymd) => {
  if (!ymd) return null;
  const d = new Date(`${ymd}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatYmd = (date) => {
  if (!date || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const DateInput = forwardRef(({ value, onClick, placeholder }, ref) => (
  <input
    ref={ref}
    readOnly
    value={value}
    onClick={onClick}
    placeholder={placeholder}
    className="w-44 px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 cursor-pointer bg-white"
  />
));

const PopperContainer = ({ children }) => (
  <div className="z-[11000] relative">{children}</div>
);

const RANGE = 10;

const yearBounds = (ymdString) => {
  const center = ymdString
    ? new Date(`${ymdString}T00:00:00`).getFullYear()
    : new Date().getFullYear();
  return {
    minDate: new Date(center - RANGE, 0, 1),
    maxDate: new Date(center + RANGE, 11, 31),
  };
};

/**
 * Shared toolbar with optional period filter, search box, add-new button, and CSV export.
 */
export default function ViewToolbar({
  searchTerm,
  onSearchChange,
  onAddNew,
  onExportCsv,
  placeholder = 'Search...',
  addLabel = '+ Add New',
  addColor = 'bg-blue-600 hover:bg-blue-700',
  period,
  onPeriodChange,
  dateFrom,
  onDateFromChange,
  dateTo,
  onDateToChange,
}) {
  const showPeriodFilter = typeof onPeriodChange === 'function';

  const sharedPickerProps = {
    dateFormat: 'dd/MM/yyyy',
    placeholderText: 'Select date',
    showPopperArrow: false,
    calendarStartDay: 1,
    showMonthDropdown: true,
    showYearDropdown: true,
    dropdownMode: 'select',
    popperContainer: PopperContainer,
    popperClassName: 'custom-datepicker toolbar-datepicker-popper',
  };

  return (
    <div className="mb-4 flex flex-wrap gap-3 items-center">
      {showPeriodFilter && (
        <>
          <div className="flex flex-col gap-0.5">
            <label className="text-[11px] font-medium text-gray-500">Period</label>
            <select
              value={period}
              onChange={(e) => onPeriodChange(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-0.5">
            <label className="text-[11px] font-medium text-gray-500">Date from</label>
            <DatePicker
              selected={parseYmd(dateFrom)}
              onChange={(d) => onDateFromChange(formatYmd(d))}
              customInput={<DateInput />}
              {...sharedPickerProps}
              {...yearBounds(dateFrom)}
            />
          </div>

          <div className="flex flex-col gap-0.5">
            <label className="text-[11px] font-medium text-gray-500">Date to</label>
            <DatePicker
              selected={parseYmd(dateTo)}
              onChange={(d) => onDateToChange(formatYmd(d))}
              customInput={<DateInput />}
              {...sharedPickerProps}
              {...yearBounds(dateTo)}
            />
          </div>
        </>
      )}

      <div className="flex flex-col gap-0.5">
        {showPeriodFilter && <label className="text-[11px] font-medium text-gray-500">Search</label>}
        <input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="px-4 py-2 text-sm w-56 border border-gray-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      <div className="flex-1" />

      <div className={`flex gap-3 ${showPeriodFilter ? 'self-end' : ''}`}>
        <button
          onClick={onAddNew}
          className={`px-5 py-2 text-white text-sm font-medium rounded-lg cursor-pointer transition-colors ${addColor}`}
        >
          {addLabel}
        </button>

        {onExportCsv && (
          <button
            onClick={onExportCsv}
            className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg cursor-pointer transition-colors"
          >
            Download CSV
          </button>
        )}
      </div>
    </div>
  );
}
